// Same-account push-operation recovery. No bearer credential is persisted here.
const KEY = 'push_registration_operation_v2';
const DEVICE = 'push_registration_device_v2';
function operationId() {
  if (crypto.randomUUID) return crypto.randomUUID();
  const bytes = crypto.getRandomValues(new Uint8Array(16));
  bytes[6] = (bytes[6] & 15) | 64; bytes[8] = (bytes[8] & 63) | 128;
  const hex = Array.from(bytes, value => value.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0,8)}-${hex.slice(8,12)}-${hex.slice(12,16)}-${hex.slice(16,20)}-${hex.slice(20)}`;
}
const base = () => `${(process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '')}/api/push/registration`;
const result = (code, retryable = false) => ({code, retryable});
export function pushAccount(access) {
  try { return JSON.parse(atob(access.split('.')[1].replace(/-/g,'+').replace(/_/g,'/'))).user_id || null; }
  catch { return null; }
}
const records = () => JSON.parse(localStorage.getItem(KEY) || '{}');
const pending = account => records()[account];
function save(account, operation) { const all=records(); all[account]=operation;localStorage.setItem(KEY,JSON.stringify(all)); }
function clear(account, id) { const all=records();if(all[account]?.operation_id===id){delete all[account];localStorage.setItem(KEY,JSON.stringify(all));} }
export const hasPushOperation = access => !!pending(pushAccount(access));
const bound = promise => new Promise((resolve,reject)=>{
  const timer=setTimeout(()=>reject(new Error('timeout')),15000);
  Promise.resolve(promise).then(resolve,reject).finally(()=>clearTimeout(timer));
});
async function request(path,access,body) {
  const response=await fetch(`${base()}${path}`,{method:body?'POST':'GET',headers:{'X-OpenRuns-Client-Protocol':'2',Authorization:`Bearer ${access}`,'Content-Type':'application/json'},...(body?{body:JSON.stringify(body)}:{})});
  if(!response.ok) throw new Error('unconfirmed');
  return response.json();
}
async function execute(account,operation,access,current) {
  // Completion remains attached to this exact live HTTP request after a UI timeout.
  // Clearing the receipt is NOT permission to display success to another account.
  const completion=request('/operations',access,operation).then(value=>{
    if(value.operation_id!==operation.operation_id || !['completed','superseded','rejected'].includes(value.state)) throw new Error('unconfirmed');
    clear(account,operation.operation_id);
    return value;
  });
  let value;
  try { value=await bound(completion); } catch { return result('upload_uncertain',true); }
  if(!current()) return result('abandoned');
  if(value.state==='rejected') return result('configuration_required');
  if(value.state!=='completed') return result('superseded',true);
  if(operation.action==='logout') return result('logged_out');
  try {
    const state=await bound(request('/current',access,operation));
    if(!current()) return result('abandoned');
    return state.registered && state.binding_revision===value.binding_revision ? result('registered') : result('superseded',true);
  } catch { return result('status_unknown',true); }
}
export async function recoverPushOperation(access,current) {
  const account=pushAccount(access), operation=pending(account);
  if(!account || !operation) return result('no_operation');
  // Replay has the original identity, payload and CAS revisions. It cannot reclaim
  // a newer binding, even when the original request has not reached the server yet.
  const recovered = await execute(account,operation,access,current);
  return recovered.code === 'logged_out' ? result('registration_needed',true) : recovered;
}
export async function changePushBinding(data,access,current,action='register') {
  const account=pushAccount(access);
  if(!account) return result('sign_in_required');
  if(action==='register' && pending(account)) return recoverPushOperation(access,current);
  let context;
  try { context=await bound(request('/context',access,data)); } catch { return result('context_failed',true); }
  if(!current()) return result('abandoned');
  if(!context.binding_revision || !context.device_revision) return result('context_failed',true);
  const operation={...data,...context,action,operation_id:operationId()};
  try { save(account,operation);localStorage.setItem(DEVICE,JSON.stringify(data)); }
  catch { return result('storage_unavailable'); }
  return execute(account,operation,access,current);
}
export async function logoutPushBinding(access, current = () => true, legacyDevice = null) {
  const account=pushAccount(access);
  let device;
  try { device=pending(account) || JSON.parse(localStorage.getItem(DEVICE)||'null') || legacyDevice; } catch { return false; }
  if(!device) return true;
  // Logout has a new CAS revision; acknowledgment fences every earlier operation
  // for the device. It never waits for an earlier upload to finish.
  const data={token:device.token,platform:'ios',environment:device.environment,device_id:device.device_id,app_version:device.app_version,locale:device.locale};
  const outcome=await changePushBinding(data,access,current,'logout');
  return outcome.code==='logged_out';
}

// Read-only status of this installation's acknowledged binding. Never replay or write here.
export async function readCurrentPushRegistration(access, current = () => true) {
  const account = pushAccount(access);
  if (!account || !current()) return null;
  let device;
  try {
    if (pending(account)) return false;
    device = JSON.parse(localStorage.getItem(DEVICE) || 'null');
    const identity = await window.Capacitor?.Plugins?.Device?.getId?.();
    if (!current()) return null;
    if (!device || device.device_id !== (identity?.identifier || identity?.uuid)) return false;
    const state = await bound(request('/current', access, device));
    if (!current()) return null;
    return state.registered === true && !!state.binding_revision;
  } catch { return null; }
}
